import { useState, useEffect, useMemo } from "react";
import FleetWebApi from "../Api/FleetWebApi";
import CircularProgress from "@mui/material/CircularProgress";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Reports from "./Reports";

const TREND_LABELS = {
  worsening: "Worsening",
  improving: "Improving",
  steady: "Steady",
};

const STATUS_TAG = "event-spammer-repeat-offenders";
const IGNORE_STATUS = "Ignore";
const STATUS_OPTIONS = ["Reached out to CS", "Fix Pending", IGNORE_STATUS];
// Row text colour per status (dark theme)
const STATUS_COLORS = {
  "Reached out to CS": "#ffeb3b", // yellow
  "Fix Pending": "#64b5f6", // blue
};
const LEGACY_STATUSES = { Whitelist: IGNORE_STATUS };
const DEFAULT_HIDDEN_COLUMNS = ["LocationName", "FirstSeen"];
// The main table shows the N most severe devices that aren't ignored; ignoring one pulls in
// the next device from the report, so it stays at N while enough devices are available
const MAX_ACTIVE_DEVICES = 50;

function formatTrend(trend) {
  return trend ? TREND_LABELS[trend] || trend : "New";
}

function formatFirstSeen(firstSeen) {
  return new Date(firstSeen).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function EventSpammerRepeatOffenders(props) {
  const [rows, setRows] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [reportTimestamp, setReportTimestamp] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);
    FleetWebApi.getEventSpammerRepeatOffenders()
      .then(async (response) => {
        const loadedRows = response.devices.map((device) => ({
          coreID: device.core_id,
          SerialNumber: device.serial_number,
          OwnerEmail: device.owner_email,
          LocationName: device.location_name,
          RunsAppeared: device.run_count + "/" + response.num_runs,
          AvgDailyCount: device.avg_count,
          TotalCount: device.total_count,
          Events: device.events.join(", "),
          Trend: formatTrend(device.trend),
          FirstSeen: formatFirstSeen(device.first_seen),
        }));
        // Load saved statuses up front so ignored devices can be split out before rendering.
        // The lookup errors when no device has a status yet, which just means "none saved".
        let loadedStatuses = {};
        try {
          loadedStatuses =
            (await FleetWebApi.checkForTaggedNote(
              loadedRows.map((row) => row.coreID),
              STATUS_TAG
            )) || {};
        } catch (statusError) {
          console.log(statusError.message);
        }
        Object.keys(loadedStatuses).forEach((coreID) => {
          const legacy = LEGACY_STATUSES[loadedStatuses[coreID]];
          if (legacy) loadedStatuses[coreID] = legacy;
        });
        if (isMounted) {
          setRows(loadedRows);
          setStatuses(loadedStatuses);
          setReportTimestamp(response.timestamp);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (isMounted) {
          setError(error.message);
          setIsLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Memoized so the tables only re-derive (and reset sort/paging) when a row actually moves.
  // `rows` keeps the report's most-severe-first order, so slicing gives the top N.
  const notIgnoredRows = useMemo(
    () => rows.filter((row) => statuses[row.coreID] !== IGNORE_STATUS),
    [rows, statuses]
  );
  const activeRows = useMemo(
    () => notIgnoredRows.slice(0, MAX_ACTIVE_DEVICES),
    [notIgnoredRows]
  );
  const ignoredRows = useMemo(
    () => rows.filter((row) => statuses[row.coreID] === IGNORE_STATUS),
    [rows, statuses]
  );

  const handleStatusChange = (coreID, status) => {
    setStatuses((prev) => ({ ...prev, [coreID]: status }));
  };

  const statusProps = {
    // Click a column name to filter by its values; Events cells hold several events
    // ("A, B"), so each event is offered as its own filter option
    enableColumnFilters: true,
    multiValueColumns: { Events: ", " },
    showStatusField: true,
    statusTag: STATUS_TAG,
    statusOptions: STATUS_OPTIONS,
    statuses: statuses,
    onStatusChange: handleStatusChange,
    statusColors: STATUS_COLORS,
  };

  return (
    <>
      {error && <p>Error Loading Data: {error}</p>}
      {!error && isLoading && (
        <>
          <p>Please wait while the repeat offenders are being found...</p>
          <CircularProgress color="secondary" />
        </>
      )}
      {!error && !isLoading && rows.length === 0 && (
        <p>No repeat offenders found.</p>
      )}
      {!error && !isLoading && rows.length > 0 && (
        <>
          {activeRows.length > 0 ? (
            <Reports
              data={activeRows}
              title={"Repeat Offenders"}
              timestamp={reportTimestamp}
              defaultHiddenColumns={DEFAULT_HIDDEN_COLUMNS}
              headerContent={
                <Typography variant="body2" color="text.secondary">
                  Showing the {activeRows.length} most severe of {notIgnoredRows.length} repeat
                  offenders (ignored devices don't count toward the {MAX_ACTIVE_DEVICES})
                </Typography>
              }
              {...statusProps}
            />
          ) : (
            <p>All repeat offenders are ignored.</p>
          )}

          {/* mx: 2 matches the side inset of the table in Reports.jsx (Grid px: 2). It's on a
              wrapper because MUI resets the Accordion's own margin to "16px 0" when expanded. */}
          <Box sx={{ mt: 3, mx: 2 }}>
          <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Ignored Devices ({ignoredRows.length})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {ignoredRows.length > 0 ? (
                <Reports
                  data={ignoredRows}
                  title={"Ignored Devices"}
                  timestamp={null}
                  defaultHiddenColumns={DEFAULT_HIDDEN_COLUMNS}
                  {...statusProps}
                />
              ) : (
                <Typography color="text.secondary">
                  No ignored devices. Set a device's status to "Ignore" to move it here.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
          </Box>
        </>
      )}
    </>
  );
}

export default EventSpammerRepeatOffenders;
